import { createContext, useContext } from 'react';
import { PersistedWorkflow } from '@cotera/sdk/core';
import { asRecord, readString } from './components/utils';
import type { WorkflowNodeViewData } from './components/node/types';
import type { WorkflowNodeDisplayOverride } from './components/node/workflow-node';
import {
  buildNodeSourceLabels,
  type SourceLabelNode,
} from './hooks/use-upstream-source-labels';
import type { NestedWorkflowTree } from './nested-workflow-tree';

const { WorkflowNodeType } = PersistedWorkflow;

const EMPTY_LABELS: ReadonlyMap<string, string> = new Map();

/**
 * Source-label maps keyed by the workflow a card belongs to.
 *
 * Each nested card resolves its refs against its OWN workflow's nodes, not the
 * page model's — a filter inside a nested workflow references source ids that
 * live only in that workflow's graph, so the page model's map renders them as
 * raw ids.
 */
const NestedSourceLabelsContext = createContext<
  ReadonlyMap<string, ReadonlyMap<string, string>>
>(new Map());

export const NestedSourceLabelsProvider = NestedSourceLabelsContext.Provider;

/** The source-label map for one owner workflow in the tree (empty when absent). */
export function useNestedSourceLabels(
  workflowId: string
): ReadonlyMap<string, string> {
  return useContext(NestedSourceLabelsContext).get(workflowId) ?? EMPTY_LABELS;
}

/**
 * Walks the tree and builds a ref-key → friendly-label map for each distinct
 * workflow, deduped by id (a workflow can appear in the tree more than once, but
 * its labels are the same wherever it sits).
 */
export function buildNestedWorkflowSourceLabels(
  tree: NestedWorkflowTree,
  agentTitleById: ReadonlyMap<string, string>
): Map<string, ReadonlyMap<string, string>> {
  const byWorkflow = new Map<string, ReadonlyMap<string, string>>();
  const visit = (node: NestedWorkflowTree): void => {
    if (!byWorkflow.has(node.workflowId)) {
      byWorkflow.set(
        node.workflowId,
        buildNodeSourceLabels(node.graph.nodes, agentTitleById)
      );
    }
    for (const link of node.links) {
      if (link.child.kind === 'workflow') {
        visit(link.child.tree);
      }
    }
  };
  visit(tree);
  return byWorkflow;
}

/**
 * A {@link WorkflowNodeDisplayOverride} that resolves an agent card's title from
 * the org agent list, so a nested agent shows its name rather than the
 * truncated-id fallback the generic card uses with no live agent.
 */
export function makeAgentDisplayOverride(
  agentTitleById: ReadonlyMap<string, string>
): WorkflowNodeDisplayOverride {
  return (node: WorkflowNodeViewData) => {
    if (node.type !== WorkflowNodeType.Agent) {
      return undefined;
    }
    const agentId = readString(asRecord(node.data), 'agentId');
    if (agentId === undefined) {
      return undefined;
    }
    const title = agentTitleById.get(agentId);
    return title === undefined ? undefined : { title };
  };
}

// `SourceLabelNode` is re-exported so callers wiring the tree can type node
// lists without reaching into the hooks module.
export type { SourceLabelNode };
